import { useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Modal as MuiModal,
  ModalProps,
} from "@mui/material";

const CLOSE_CONFIRMATION_MESSAGE =
  "Are you sure you want to close this window?";

type ModalCloseHandler = NonNullable<ModalProps["onClose"]>;

type PendingClose = {
  event: Parameters<ModalCloseHandler>[0];
  reason: Parameters<ModalCloseHandler>[1];
};

const ConfirmableModal = ({
  onClose,
  children,
  sx = [],
  ...props
}: ModalProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingCloseRef = useRef<PendingClose | null>(null);

  const handleClose: ModalCloseHandler = (event, reason) => {
    if (reason !== "backdropClick") {
      onClose?.(event, reason);
      return;
    }

    if (confirmOpen) {
      return;
    }

    pendingCloseRef.current = { event, reason };
    setConfirmOpen(true);
  };

  const handleCancelClose = () => {
    pendingCloseRef.current = null;
    setConfirmOpen(false);
  };

  const handleConfirmClose = () => {
    const pendingClose = pendingCloseRef.current;
    pendingCloseRef.current = null;
    setConfirmOpen(false);

    if (pendingClose) {
      onClose?.(pendingClose.event, pendingClose.reason);
    }
  };

  return (
    <>
      <MuiModal
        {...props}
        onClose={handleClose}
        sx={[
          ...(Array.isArray(sx) ? sx : [sx]),
          {
            "& > .MuiBox-root, & > .MuiPaper-root": {
              maxWidth: "calc(100vw - 32px)",
              maxHeight: "calc(100dvh - 32px)",
              overflowY: "auto",
              boxSizing: "border-box",
              "@media (max-width: 599.95px)": { padding: 16 },
            },
          },
        ]}
      >
        {children}
      </MuiModal>
      <Dialog open={confirmOpen} onClose={undefined} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm close</DialogTitle>
        <DialogContent>
          <DialogContentText>{CLOSE_CONFIRMATION_MESSAGE}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose}>No / Cancel</Button>
          <Button
            onClick={handleConfirmClose}
            variant="contained"
            color="primary"
            autoFocus
          >
            Yes / Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfirmableModal;
